import React, { useCallback, useEffect, useState } from "react";
import { Resizable } from "re-resizable";
import { Children } from 'react';
import { useMediaQuery } from 'react-responsive';

const DEFAULT_INVOICE_WIDTH = 896;
const DEFAULT_COLUMN_WIDTH = 150;

/**
 * localStorage throws outright in Safari private mode, and the stored value is
 * whatever an earlier build - or a hand-edited devtools session - left behind.
 * Anything that is not a usable object is treated as "nothing saved" rather
 * than taking the whole pane down with it.
 */
const readStoredSize = (key) => {
   try {
      const raw = window.localStorage.getItem(key);

      if (!raw) return null;

      const parsed = JSON.parse(raw);

      return parsed && typeof parsed === 'object' ? parsed : null;
   } catch (error) {
      return null;
   }
};

const writeStoredSize = (key, size) => {
   try {
      window.localStorage.setItem(key, JSON.stringify(size));
   } catch (error) {
      // A blocked or full store only costs the user a remembered width.
   }
};

/**
 * Bounds arrive as CSS lengths ("45vw", "300px", 300). Resolving them to pixels
 * lets a restored width be held to the same bounds re-resizable enforces while
 * dragging. Without it a width saved on a wider screen - or the 896px default
 * itself, which is wider than 45vw below a ~1990px viewport - sits above max:
 * CSS clamps what you see while the stored number stays too large, so the first
 * drag jumps to a width the user never chose.
 */
const toPixels = (value) => {
   if (value == null) return null;
   if (typeof value === 'number') return Number.isFinite(value) ? value : null;

   const match = String(value).trim().match(/^(-?\d*\.?\d+)(px|vw)?$/);

   if (!match) return null;

   const amount = parseFloat(match[1]);

   if (!Number.isFinite(amount)) return null;

   return match[2] === 'vw' ? (window.innerWidth * amount) / 100 : amount;
};

/**
 * A pane the user can widen by dragging its right edge.
 *
 * @param {object} props
 * @param {string} props.type - "Invoice" for a document pane, anything else for a column.
 * @param {string} [props.column] - Column identifier, used to build the default storage key.
 * @param {object} [props.border] - Per-side CSS border overrides.
 * @param {string|number} [props.maxWidth] - Upper bound, as a CSS length.
 * @param {string|number} [props.minWidth] - Lower bound, as a CSS length.
 * @param {string|number} [props.height] - Pane height. Defaults suit a full page, so a
 *   pane inside a dialog should pass "100%" rather than overflow it.
 * @param {string} [props.storageKey] - Where the width is remembered. Panes sharing a key
 *   share the preference; pass a distinct one for unrelated content.
 * @param {object} [props.enable] - re-resizable handle map. Only the right edge by default.
 */
export default function CustomResizable({
   type,
   column,
   border,
   children,
   maxWidth = null,
   minWidth = null,
   height = null,
   storageKey = null,
   enable = null,
}) {
   const isMobile = useMediaQuery({ maxWidth: 768 });

   const isInvoice = type === "Invoice";
   const key = storageKey || (isInvoice ? "InvoiceResized" : "ColumnResized-" + column);

   const clampWidth = useCallback((width) => {
      if (typeof width !== 'number' || !Number.isFinite(width)) return width;

      const max = toPixels(maxWidth);
      const min = toPixels(minWidth);

      let next = width;

      if (max != null) next = Math.min(next, max);
      if (min != null) next = Math.max(next, min);

      return next;
   }, [maxWidth, minWidth]);

   // Only the width was ever resizable, so only the width is restored; a stored
   // height would just be a stale copy of whatever the default was when it was
   // written, and would quietly override the height a caller asks for.
   const [width, setWidth] = useState(() => {
      if (isMobile && isInvoice) return "100%";

      const stored = readStoredSize(key);
      const fallback = isInvoice ? DEFAULT_INVOICE_WIDTH : DEFAULT_COLUMN_WIDTH;

      return clampWidth(typeof stored?.width === 'number' ? stored.width : fallback);
   });

   // Re-clamp when the window changes size, so a pane widened on a large screen
   // does not stay out of bounds once the viewport shrinks.
   useEffect(() => {
      const reclamp = () => setWidth((current) => clampWidth(current));

      reclamp();
      window.addEventListener('resize', reclamp);

      return () => window.removeEventListener('resize', reclamp);
   }, [clampWidth]);

   const paneHeight = height ?? (isMobile && isInvoice ? "100vh" : (isInvoice ? "90vh" : "5vh"));

   return (
      <Resizable
         // Keep width constraints controlled by the parent so invoice layouts stay predictable.
         minWidth={minWidth ?? undefined}
         // Leave max width unlimited by default; enforce only when parent passes maxWidth.
         maxWidth={maxWidth ?? undefined}
         // These panes are docked to the left of their row, so the right edge is
         // the only one worth dragging. The other seven handles offered cursors
         // for gestures that either resized from the wrong side or, vertically,
         // did nothing at all.
         enable={enable ?? { right: true }}
         style={{
            "borderTop": border && border.borderTop ? border.borderTop : "1px solid black",
            "borderRight": border && border.borderRight ? border.borderRight : "1px solid black",
            // Previously spelled "borderDown", which is not a CSS property and so
            // never rendered. Defaulting to none keeps the pane looking as it did.
            "borderBottom": border && border.borderBottom ? border.borderBottom : "none",
            "borderLeft": border && border.borderLeft ? border.borderLeft : "1px solid black"
         }}
         size={{ width: width, height: paneHeight }}
         onResizeStop={(e, direction, ref, d) => {
            const base = typeof width === 'number' ? width : ref.offsetWidth;
            const next = clampWidth(base + d.width);

            setWidth(next);
            writeStoredSize(key, { width: next });
         }}>
            {
            // height:100% keeps a child's own height:100% resolvable. Without it
            // the chain breaks here and a replaced element - the PDF <object> -
            // collapses to its 150px default inside a full-height pane. Every
            // caller passes a single child; several would each claim full height.
            Children.map( children, child =>
                        <div style={{ height: '100%', minHeight: 0 }}>
                        {child}
                        </div>
                        )
            }
      </Resizable>
   );
}
